import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOrganizations, createOrganization, deleteOrganization, updateOrganization, getOrganizationHierarchy, getAllOrganizations } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building2, 
  Plus, 
  ChevronRight, 
  ChevronDown,
  MapPin,
  Mail,
  Phone,
  Users,
  Trash2,
  Edit,
  Dumbbell,
  Trophy,
  Search,
  Filter,
  X,
  LayoutGrid,
  List,
  Loader2,
  Medal,
  ExternalLink
} from 'lucide-react';
import { cn } from '../lib/utils';

const ORG_TYPE_OPTIONS = [
  { value: 'sports', label: 'Sports Organization', icon: Trophy },
  { value: 'fitness', label: 'Fitness Organization', icon: Dumbbell },
  { value: 'professional_sports', label: 'Professional Sports', icon: Medal },
];

const LEVEL_OPTIONS = [
  { value: 'national', label: 'National' },
  { value: 'regional', label: 'Regional' },
  { value: 'state', label: 'State' },
  { value: 'local', label: 'Local' },
  { value: 'franchise', label: 'Franchise' },
  { value: 'franchisee', label: 'Franchisee' },
];

const LEVEL_COLORS = {
  national: 'bg-primary/20 text-primary border-primary/30',
  regional: 'bg-[#9B59B6]/20 text-[#9B59B6] border-[#9B59B6]/30',
  state: 'bg-[#3498DB]/20 text-[#3498DB] border-[#3498DB]/30',
  local: 'bg-[#2ECC71]/20 text-[#2ECC71] border-[#2ECC71]/30',
  franchise: 'bg-[#E67E22]/20 text-[#E67E22] border-[#E67E22]/30',
  franchisee: 'bg-[#1ABC9C]/20 text-[#1ABC9C] border-[#1ABC9C]/30',
};

const ORG_TYPE_COLORS = {
  sports: 'bg-blue-500/20 text-blue-600 border-blue-500/30',
  fitness: 'bg-orange-500/20 text-orange-600 border-orange-500/30',
  professional_sports: 'bg-purple-500/20 text-purple-600 border-purple-500/30',
};

const OrganizationTreeItem = ({ org, level = 0, onRefresh, onEdit, onNavigate }) => {
  const [expanded, setExpanded] = useState(true); // Default expanded to show full hierarchy
  const [children, setChildren] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const { isSuperAdmin } = useAuth();

  // Auto-fetch children on mount if org has children
  useEffect(() => {
    if (org.children_count > 0 && children.length === 0) {
      fetchChildren();
    }
  }, [org.id, org.children_count]);

  const fetchChildren = async () => {
    if (org.children_count > 0 && children.length === 0) {
      setLoadingChildren(true);
      try {
        const response = await getOrganizations({ parent_id: org.id });
        setChildren(response.data);
      } catch (error) {
        console.error('Failed to fetch children');
      } finally {
        setLoadingChildren(false);
      }
    }
  };

  const handleExpand = (e) => {
    e.stopPropagation();
    if (!expanded && org.children_count > 0) {
      fetchChildren();
    }
    setExpanded(!expanded);
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!confirm('Are you sure you want to delete this organization?')) return;
    try {
      await deleteOrganization(org.id);
      toast.success('Organization deleted');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="relative">
      {/* Tree line */}
      {level > 0 && (
        <div 
          className="absolute left-0 top-0 bottom-0 border-l border-white/10"
          style={{ left: `${(level - 1) * 24 + 12}px` }}
        />
      )}
      
      <div 
        className="flex items-center gap-2 p-3 rounded-md hover:bg-secondary/30 transition-colors group cursor-pointer"
        style={{ paddingLeft: `${level * 24 + 12}px` }}
        onClick={() => onNavigate(org.id)}
        data-testid={`org-row-${org.id}`}
      >
        {org.children_count > 0 ? (
          <button
            onClick={handleExpand}
            className="w-5 h-5 flex items-center justify-center text-muted-foreground hover:text-foreground"
          >
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        ) : (
          <div className="w-5" />
        )}
        
        <div className="w-8 h-8 rounded bg-secondary/50 flex items-center justify-center">
          <Building2 className="w-4 h-4 text-muted-foreground" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate hover:text-primary transition-colors">{org.name}</span>
            <Badge className={`text-xs uppercase ${ORG_TYPE_COLORS[org.org_type] || ORG_TYPE_COLORS.sports}`}>
              {org.org_type === 'fitness' ? <Dumbbell className="w-3 h-3 mr-1" /> : 
               org.org_type === 'professional_sports' ? <Medal className="w-3 h-3 mr-1" /> : 
               <Trophy className="w-3 h-3 mr-1" />}
              {org.org_type === 'professional_sports' ? 'Pro Sports' : (org.org_type || 'sports')}
            </Badge>
            <Badge className={`text-xs uppercase ${LEVEL_COLORS[org.level] || ''}`}>
              {org.level}
            </Badge>
            {org.children_count > 0 && (
              <span className="text-xs text-muted-foreground">
                ({org.children_count} sub-org{org.children_count > 1 ? 's' : ''})
              </span>
            )}
          </div>
          {org.city && org.state && (
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
              <MapPin className="w-3 h-3" />
              {org.city}, {org.state}
            </p>
          )}
        </div>

        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8"
            onClick={(e) => { e.stopPropagation(); onNavigate(org.id); }}
            title="View Details"
            data-testid={`view-org-${org.id}`}
          >
            <ExternalLink className="w-4 h-4" />
          </Button>
          {isSuperAdmin() && (
            <>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={(e) => { e.stopPropagation(); onEdit(org); }}
                data-testid={`edit-org-${org.id}`}
              >
                <Edit className="w-4 h-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 text-destructive hover:text-destructive"
                onClick={handleDelete}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {expanded && (
        <div className="relative">
          {loadingChildren ? (
            <div className="py-4 text-center text-muted-foreground" style={{ paddingLeft: `${(level + 1) * 24 + 12}px` }}>
              Loading...
            </div>
          ) : (
            children.map((child) => (
              <OrganizationTreeItem 
                key={child.id} 
                org={child} 
                level={level + 1} 
                onRefresh={onRefresh}
                onEdit={onEdit}
                onNavigate={onNavigate}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default function Organizations() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [allOrgs, setAllOrgs] = useState([]);
  const [filteredOrgs, setFilteredOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [saving, setSaving] = useState(false);
  const { isSuperAdmin, isOrgAdmin } = useAuth();

  // Filter states
  const [filterType, setFilterType] = useState('all');
  const [filterLevel, setFilterLevel] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const handleNavigateToOrg = (orgId) => {
    navigate(`/organizations/${orgId}`);
  };

  const [newOrg, setNewOrg] = useState({
    name: '',
    org_type: 'sports',
    level: 'local',
    parent_id: null,
    address: '',
    city: '',
    state: '',
    zip_code: '',
    contact_email: '',
    contact_phone: '',
  });

  const [editOrg, setEditOrg] = useState({
    name: '',
    org_type: 'sports',
    level: 'local',
    parent_id: null,
    address: '',
    city: '',
    state: '',
    zip_code: '',
    contact_email: '',
    contact_phone: '',
  });

  useEffect(() => {
    fetchOrganizations();
  }, []);

  // Apply filters when filter state or organizations change
  useEffect(() => {
    let filtered = [...allOrgs];
    
    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(org => (org.org_type || 'sports') === filterType);
    }
    
    // Filter by level
    if (filterLevel !== 'all') {
      filtered = filtered.filter(org => org.level === filterLevel);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(org => 
        org.name.toLowerCase().includes(query) ||
        org.city?.toLowerCase().includes(query) ||
        org.state?.toLowerCase().includes(query)
      );
    }
    
    // Get root organizations from filtered results
    const rootOrgs = filtered.filter(org => !org.parent_id);
    setFilteredOrgs(rootOrgs);
  }, [allOrgs, filterType, filterLevel, searchQuery]);

  const fetchOrganizations = async () => {
    try {
      // Get all organizations
      const response = await getOrganizations();
      const rootOrgs = response.data.filter(org => !org.parent_id);
      setOrganizations(rootOrgs);
      setAllOrgs(response.data);
      setFilteredOrgs(rootOrgs);
    } catch (error) {
      toast.error('Failed to fetch organizations');
    } finally {
      setLoading(false);
    }
  };

  // Get counts for filter badges
  const getTypeCounts = () => {
    const sports = allOrgs.filter(o => (o.org_type || 'sports') === 'sports').length;
    const fitness = allOrgs.filter(o => o.org_type === 'fitness').length;
    const professional_sports = allOrgs.filter(o => o.org_type === 'professional_sports').length;
    return { sports, fitness, professional_sports, total: allOrgs.length };
  };

  const getLevelCounts = () => {
    const counts = {};
    LEVEL_OPTIONS.forEach(opt => {
      counts[opt.value] = allOrgs.filter(o => o.level === opt.value).length;
    });
    return counts;
  };

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    try {
      await createOrganization({
        ...newOrg,
        parent_id: newOrg.parent_id || null,
      });
      toast.success('Organization created');
      setShowCreateDialog(false);
      setNewOrg({
        name: '',
        org_type: 'sports',
        level: 'local',
        parent_id: null,
        address: '',
        city: '',
        state: '',
        zip_code: '',
        contact_email: '',
        contact_phone: '',
      });
      fetchOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create organization');
    }
  };

  const handleOpenEdit = (org) => {
    setEditingOrg(org);
    setEditOrg({
      name: org.name || '',
      org_type: org.org_type || 'sports',
      level: org.level || 'local',
      parent_id: org.parent_id || null,
      address: org.address || '',
      city: org.city || '',
      state: org.state || '',
      zip_code: org.zip_code || '',
      contact_email: org.contact_email || '',
      contact_phone: org.contact_phone || '',
    });
    setShowEditDialog(true);
  };

  const handleUpdateOrg = async (e) => {
    e.preventDefault();
    if (!editingOrg) return;
    
    setSaving(true);
    try {
      await updateOrganization(editingOrg.id, {
        ...editOrg,
        parent_id: editOrg.parent_id || null,
      });
      toast.success('Organization updated');
      setShowEditDialog(false);
      setEditingOrg(null);
      fetchOrganizations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update organization');
    } finally {
      setSaving(false);
    }
  };

  const clearFilters = () => {
    setFilterType('all');
    setFilterLevel('all');
    setSearchQuery('');
  };

  const hasActiveFilters = filterType !== 'all' || filterLevel !== 'all' || searchQuery.trim() !== '';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  const typeCounts = getTypeCounts();
  const levelCounts = getLevelCounts();

  return (
    <div data-testid="organizations-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
            Organizations
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your organization hierarchy
          </p>
        </div>
        {isOrgAdmin() && (
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button data-testid="create-org-btn" className="btn-primary-glow">
                <Plus className="w-4 h-4 mr-2" />
                Add Organization
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-['Barlow_Condensed'] uppercase tracking-tight">
                  Create Organization
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateOrg} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>Organization Name</Label>
                    <Input
                      value={newOrg.name}
                      onChange={(e) => setNewOrg({...newOrg, name: e.target.value})}
                      placeholder="Organization name"
                      required
                      className="bg-secondary/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Organization Type</Label>
                    <Select 
                      value={newOrg.org_type} 
                      onValueChange={(v) => setNewOrg({...newOrg, org_type: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ORG_TYPE_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <div className="flex items-center gap-2">
                              <opt.icon className="w-4 h-4" />
                              {opt.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Level</Label>
                    <Select 
                      value={newOrg.level} 
                      onValueChange={(v) => setNewOrg({...newOrg, level: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LEVEL_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Parent Organization</Label>
                    <Select 
                      value={newOrg.parent_id || 'root'} 
                      onValueChange={(v) => setNewOrg({...newOrg, parent_id: v === 'root' ? null : v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="None (Root)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="root">None (Root)</SelectItem>
                        {allOrgs.map(org => (
                          <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Address</Label>
                    <Input
                      value={newOrg.address}
                      onChange={(e) => setNewOrg({...newOrg, address: e.target.value})}
                      placeholder="Street address"
                      className="bg-secondary/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={newOrg.city}
                      onChange={(e) => setNewOrg({...newOrg, city: e.target.value})}
                      placeholder="City"
                      className="bg-secondary/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      value={newOrg.state}
                      onChange={(e) => setNewOrg({...newOrg, state: e.target.value})}
                      placeholder="State"
                      className="bg-secondary/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Email</Label>
                    <Input
                      type="email"
                      value={newOrg.contact_email}
                      onChange={(e) => setNewOrg({...newOrg, contact_email: e.target.value})}
                      placeholder="email@org.com"
                      className="bg-secondary/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contact Phone</Label>
                    <Input
                      value={newOrg.contact_phone}
                      onChange={(e) => setNewOrg({...newOrg, contact_phone: e.target.value})}
                      placeholder="(555) 555-5555"
                      className="bg-secondary/50"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full btn-primary-glow">
                  Create Organization
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}

        {/* Edit Organization Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="bg-card border-border sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Organization</DialogTitle>
              <DialogDescription>
                Update the details for {editingOrg?.name}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateOrg} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label>Organization Name *</Label>
                  <Input
                    value={editOrg.name}
                    onChange={(e) => setEditOrg({...editOrg, name: e.target.value})}
                    placeholder="Organization name"
                    required
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Organization Type</Label>
                  <Select 
                    value={editOrg.org_type} 
                    onValueChange={(v) => setEditOrg({...editOrg, org_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ORG_TYPE_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Hierarchy Level</Label>
                  <Select 
                    value={editOrg.level} 
                    onValueChange={(v) => setEditOrg({...editOrg, level: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LEVEL_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Parent Organization</Label>
                  <Select 
                    value={editOrg.parent_id || 'root'} 
                    onValueChange={(v) => setEditOrg({...editOrg, parent_id: v === 'root' ? null : v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="None (Root)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="root">None (Root)</SelectItem>
                      {allOrgs.filter(o => o.id !== editingOrg?.id).map(org => (
                        <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Address</Label>
                  <Input
                    value={editOrg.address}
                    onChange={(e) => setEditOrg({...editOrg, address: e.target.value})}
                    placeholder="Street address"
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>City</Label>
                  <Input
                    value={editOrg.city}
                    onChange={(e) => setEditOrg({...editOrg, city: e.target.value})}
                    placeholder="City"
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>State</Label>
                  <Input
                    value={editOrg.state}
                    onChange={(e) => setEditOrg({...editOrg, state: e.target.value})}
                    placeholder="State"
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Email</Label>
                  <Input
                    type="email"
                    value={editOrg.contact_email}
                    onChange={(e) => setEditOrg({...editOrg, contact_email: e.target.value})}
                    placeholder="email@org.com"
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Phone</Label>
                  <Input
                    value={editOrg.contact_phone}
                    onChange={(e) => setEditOrg({...editOrg, contact_phone: e.target.value})}
                    placeholder="(555) 555-5555"
                    className="bg-secondary/50"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} className="btn-primary-glow">
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter Bar */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search organizations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-secondary/50"
              />
            </div>
            
            {/* Type Filter */}
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full lg:w-[200px]">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  <SelectValue placeholder="All Types" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <div className="flex items-center justify-between w-full">
                    <span>All Types</span>
                    <Badge variant="secondary" className="ml-2">{typeCounts.total}</Badge>
                  </div>
                </SelectItem>
                <SelectItem value="sports">
                  <div className="flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-blue-500" />
                    <span>Sports</span>
                    <Badge variant="secondary" className="ml-auto">{typeCounts.sports}</Badge>
                  </div>
                </SelectItem>
                <SelectItem value="fitness">
                  <div className="flex items-center gap-2">
                    <Dumbbell className="w-4 h-4 text-orange-500" />
                    <span>Fitness</span>
                    <Badge variant="secondary" className="ml-auto">{typeCounts.fitness}</Badge>
                  </div>
                </SelectItem>
                <SelectItem value="professional_sports">
                  <div className="flex items-center gap-2">
                    <Medal className="w-4 h-4 text-purple-500" />
                    <span>Pro Sports</span>
                    <Badge variant="secondary" className="ml-auto">{typeCounts.professional_sports}</Badge>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            
            {/* Level Filter */}
            <Select value={filterLevel} onValueChange={setFilterLevel}>
              <SelectTrigger className="w-full lg:w-[200px]">
                <div className="flex items-center gap-2">
                  <LayoutGrid className="w-4 h-4" />
                  <SelectValue placeholder="All Levels" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <div className="flex items-center justify-between w-full">
                    <span>All Levels</span>
                    <Badge variant="secondary" className="ml-2">{typeCounts.total}</Badge>
                  </div>
                </SelectItem>
                {LEVEL_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <div className="flex items-center justify-between w-full">
                      <Badge className={`${LEVEL_COLORS[opt.value]} uppercase text-xs mr-2`}>
                        {opt.label}
                      </Badge>
                      <Badge variant="secondary">{levelCounts[opt.value] || 0}</Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-2">
                <X className="w-4 h-4" />
                Clear Filters
              </Button>
            )}
          </div>
          
          {/* Active Filter Tags */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-border">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {filterType !== 'all' && (
                <Badge variant="secondary" className="gap-1">
                  {filterType === 'sports' ? <Trophy className="w-3 h-3" /> : 
                   filterType === 'professional_sports' ? <Medal className="w-3 h-3" /> : 
                   <Dumbbell className="w-3 h-3" />}
                  {filterType === 'sports' ? 'Sports' : filterType === 'professional_sports' ? 'Pro Sports' : 'Fitness'}
                  <button onClick={() => setFilterType('all')} className="ml-1 hover:text-destructive">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}
              {filterLevel !== 'all' && (
                <Badge className={`${LEVEL_COLORS[filterLevel]} gap-1`}>
                  {LEVEL_OPTIONS.find(o => o.value === filterLevel)?.label}
                  <button onClick={() => setFilterLevel('all')} className="ml-1 hover:text-destructive">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}
              {searchQuery.trim() && (
                <Badge variant="secondary" className="gap-1">
                  Search: "{searchQuery}"
                  <button onClick={() => setSearchQuery('')} className="ml-1 hover:text-destructive">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-primary">{typeCounts.total}</div>
            <div className="text-sm text-muted-foreground">Total Organizations</div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <Trophy className="w-5 h-5 text-blue-500" />
              <span className="text-3xl font-bold text-blue-500">{typeCounts.sports}</span>
            </div>
            <div className="text-sm text-muted-foreground">Sports Orgs</div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <Dumbbell className="w-5 h-5 text-orange-500" />
              <span className="text-3xl font-bold text-orange-500">{typeCounts.fitness}</span>
            </div>
            <div className="text-sm text-muted-foreground">Fitness Orgs</div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <Medal className="w-5 h-5 text-purple-500" />
              <span className="text-3xl font-bold text-purple-500">{typeCounts.professional_sports}</span>
            </div>
            <div className="text-sm text-muted-foreground">Pro Sports Orgs</div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-muted-foreground">{filteredOrgs.length}</div>
            <div className="text-sm text-muted-foreground">Showing</div>
          </CardContent>
        </Card>
      </div>

      {/* Level Legend */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
            <LayoutGrid className="w-4 h-4" />
            <span>Hierarchy Levels:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {LEVEL_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setFilterLevel(filterLevel === opt.value ? 'all' : opt.value)}
                className={cn(
                  "transition-all",
                  filterLevel === opt.value && "ring-2 ring-primary ring-offset-2 ring-offset-background rounded"
                )}
              >
                <Badge className={`${LEVEL_COLORS[opt.value]} uppercase text-xs cursor-pointer hover:opacity-80`}>
                  {opt.label} ({levelCounts[opt.value] || 0})
                </Badge>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Organization Tree */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg font-['Barlow_Condensed'] uppercase tracking-tight flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Organization Hierarchy
            {hasActiveFilters && (
              <Badge variant="secondary" className="ml-2">
                {filteredOrgs.length} result{filteredOrgs.length !== 1 ? 's' : ''}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-2">
          {filteredOrgs.length > 0 ? (
            <div className="divide-y divide-border/50">
              {filteredOrgs.map((org) => (
                <OrganizationTreeItem 
                  key={org.id} 
                  org={org} 
                  onRefresh={fetchOrganizations}
                  onEdit={handleOpenEdit}
                  onNavigate={handleNavigateToOrg}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
              {hasActiveFilters ? (
                <>
                  <p>No organizations match your filters</p>
                  <Button variant="link" onClick={clearFilters} className="mt-2">
                    Clear all filters
                  </Button>
                </>
              ) : (
                <>
                  <p>No organizations yet</p>
                  <p className="text-sm mt-1">Create your first organization to get started</p>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
